import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { ShiftCard } from '../components/ShiftCard';
import { shiftsService, bookingsService, volunteerApplicationService, authService, sickNoteService } from '../services/api';
import { Shift, Booking, User } from '../types/index';
import { useAuth } from '../hooks/useAuth';

export function Dashboard() {
  const { user: authUser, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(authUser);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [applicationStatus, setApplicationStatus] = useState<string | null>(null);
  const [redirectChecked, setRedirectChecked] = useState(false);
  const [selectedShiftIds, setSelectedShiftIds] = useState<Set<number>>(new Set());
  const [isBooking, setIsBooking] = useState(false);
  const [constraints, setConstraints] = useState<any[]>([]);
  const [validation, setValidation] = useState<any>(null);
  const [displaySettings, setDisplaySettings] = useState({ show_shift_title: true, show_shift_location: true, show_available_spots: true, booking_enabled: 'booking-enabled' as 'no-access' | 'view-only' | 'booking-enabled', sick_note_enabled: false, booking_capacity_percent: 100, whatsapp_group_join_url: '', whatsapp_group_confirmation_code: '' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsappConfirmationCode, setWhatsappConfirmationCode] = useState('');
  const [whatsappConfirmationError, setWhatsappConfirmationError] = useState('');
  const [whatsappConfirmationLoading, setWhatsappConfirmationLoading] = useState(false);
  const [pendingSickNotes, setPendingSickNotes] = useState(0);
  const [sickNotesDebug, setSickNotesDebug] = useState<{ total: number; pending: number } | null>(null);

  useEffect(() => {
    // Wait for auth check to finish before redirecting
    if (authLoading) return;

    if (!authUser) {
      navigate('/login');
      return;
    }

    // Only check once to prevent infinite redirects
    if (!redirectChecked) {
      checkApplicationStatus();
      setRedirectChecked(true);
    }
  }, [authUser, authLoading]);

  useEffect(() => {
    if (!authUser) return;
    loadPendingSickNotes();
  }, [authUser]);

  const loadPendingSickNotes = async () => {
    try {
      const sickNotesRes = await sickNoteService.getMine();
      const notes = sickNotesRes.data?.notes || [];
      const pendingCount = notes.filter((note: any) => {
        const status = String(note.status || '').toLowerCase().trim();
        return status === 'pending' || status === 'doctor_required';
      }).length;
      setPendingSickNotes(pendingCount);
      setSickNotesDebug({ total: notes.length, pending: pendingCount });
    } catch {
      // Ignore errors for dashboard badge
    }
  };

  const checkApplicationStatus = async () => {
    try {
      // Refresh user data to get latest withdrawn status
      const userRes = await authService.getCurrentUser();
      setUser(userRes.data);

      // Redirect withdrawn volunteers back to dashboard
      if (userRes.data.withdrawn) {
        navigate('/volunteer-dashboard');
        return;
      }

      // Redirect guest users to signup
      if (userRes.data.role === 'guest') {
        navigate('/signup');
        return;
      }

      const res = await volunteerApplicationService.getStatus();
      
      if (res.data.status === 'rejected') {
        setError('Your volunteer application was rejected. Please contact us.');
        setLoading(false);
        return;
      }

      if (res.data.status !== 'approved') {
        navigate('/signup');
        return;
      }

      setApplicationStatus('approved');
      loadData(userRes.data);
    } catch (err) {
      navigate('/signup');
    }
  };

  const loadData = async (currentUser?: User | null) => {
    // Fire-and-forget page view log
    authService.logEvent('page_view_shifts').catch(() => {});

    try {
      const [shiftsRes, bookingsRes, constraintsRes, settingsRes, sickNotesRes] = await Promise.all([
        shiftsService.getAllShifts(),
        bookingsService.getUserBookings(),
        bookingsService.getConstraints(),
        shiftsService.getSettings(),
        sickNoteService.getMine().catch(() => ({ data: { notes: [] } })),
      ]);

      setShifts(shiftsRes.data);
      setBookings(bookingsRes.data);
      setConstraints(constraintsRes.data);
      setDisplaySettings({ 
        show_shift_title: settingsRes.data?.show_shift_title ?? true, 
        show_shift_location: settingsRes.data?.show_shift_location ?? true,
        show_available_spots: settingsRes.data?.show_available_spots ?? true,
        booking_enabled: settingsRes.data?.booking_enabled ?? 'booking-enabled',
        sick_note_enabled: settingsRes.data?.sick_note_enabled ?? false,
        booking_capacity_percent: settingsRes.data?.booking_capacity_percent ?? 100,
        whatsapp_group_join_url: settingsRes.data?.whatsapp_group_join_url ?? '',
        whatsapp_group_confirmation_code: settingsRes.data?.whatsapp_group_confirmation_code ?? '',
      });
      const isActive = currentUser?.role === 'volunteer' || currentUser?.role === 'supervisor';
      setShowWhatsAppModal(!!currentUser?.not_found_on_whatsapp && !!isActive && !!settingsRes.data?.whatsapp_group_join_url?.trim());
      const pendingCount = (sickNotesRes.data?.notes || []).filter((note: any) => {
        const status = String(note.status || '').toLowerCase().trim();
        return status === 'pending' || status === 'doctor_required';
      }).length;
      setPendingSickNotes(pendingCount);
    } catch (err: any) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Validate selection whenever it changes
  useEffect(() => {
    const validateSelection = async () => {
      if (selectedShiftIds.size === 0) {
        setValidation(null);
        return;
      }

      try {
        const result = await bookingsService.validateSelection(Array.from(selectedShiftIds));
        setValidation(result.data);
      } catch (err) {
        console.error('Validation error:', err);
      }
    };

    validateSelection();
  }, [selectedShiftIds]);

  const hasMinTimeViolation = (shiftId: number) => {
    // Check if this shift violates min hours between shifts
    if (!validation?.constraints) return false;
    
    // Look for the "Minimum Hours Between Shifts" constraint (constraint_id: -1)
    const minHoursConstraint = validation.constraints.find(
      (c: any) => c.constraint_id === -1 || c.name === 'Minimum Hours Between Shifts'
    );
    
    // If constraint doesn't exist or is satisfied, no violation
    if (!minHoursConstraint || minHoursConstraint.satisfied) return false;
    
    // Get all selected shifts sorted by start time
    const selectedShifts = Array.from(selectedShiftIds)
      .map(id => shifts.find(s => s.id === id))
      .filter(Boolean)
      .sort((a, b) => new Date(a!.start_time).getTime() - new Date(b!.start_time).getTime());
    
    // Check if any adjacent pairs violate the constraint
    // Parse the minimum hours from the message or use a default (7 hours based on logs)
    let minHours = 7; // Default from API
    if (minHoursConstraint.message?.includes('7h')) {
      minHours = 7;
    }
    
    for (let i = 0; i < selectedShifts.length - 1; i++) {
      const endTime = new Date(selectedShifts[i]!.end_time);
      const nextStartTime = new Date(selectedShifts[i + 1]!.start_time);
      const hoursBetween = (nextStartTime.getTime() - endTime.getTime()) / (1000 * 60 * 60);
      
      // If this pair violates the constraint, mark both shifts
      if (hoursBetween < minHours) {
        if (selectedShifts[i]!.id === shiftId || selectedShifts[i + 1]!.id === shiftId) {
          return true;
        }
      }
    }
    
    return false;
  };

  const toggleShiftSelection = (shiftId: number) => {
    setSelectedShiftIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(shiftId)) {
        newSet.delete(shiftId);
      } else {
        newSet.add(shiftId);
      }
      return newSet;
    });
  };

  const handleBookSelectedShifts = async () => {
    if (selectedShiftIds.size === 0) {
      setModalMessage('Please select at least one shift to book');
      setShowErrorModal(true);
      return;
    }

    // Check if validation passed
    if (!validation || !validation.valid) {
      setModalMessage('Please satisfy all requirements before booking');
      setShowErrorModal(true);
      return;
    }

    setShowConfirmModal(true);
  };

  const confirmBooking = async () => {
    setShowConfirmModal(false);
    setIsBooking(true);
    setError('');

    try {
      await bookingsService.bookMultipleShifts(Array.from(selectedShiftIds));
      await loadData();
      setSelectedShiftIds(new Set());
      setValidation(null);
      setShowSuccessModal(true);
    } catch (err: any) {
      setModalMessage(err.response?.data?.error || 'Failed to book shifts');
      // Refresh data so the UI reflects current availability (no partial bookings should be visible)
      try {
        await loadData();
      } catch (loadErr) {
        console.error('Failed to reload data after booking error', loadErr);
      }
      setShowErrorModal(true);
    } finally {
      setIsBooking(false);
    }
  };

  const handleWhatsAppConfirmation = async (e: React.FormEvent) => {
    e.preventDefault();
    setWhatsappConfirmationError('');

    if (!whatsappConfirmationCode.trim()) {
      setWhatsappConfirmationError('Please enter the confirmation code from the WhatsApp group description.');
      return;
    }

    setWhatsappConfirmationLoading(true);
    try {
      const res = await authService.confirmWhatsAppGroupJoin(whatsappConfirmationCode.trim());
      if (res.data?.user) {
        setShowWhatsAppModal(false);
        setWhatsappConfirmationCode('');
        setUser(res.data.user);
      }
    } catch (err: any) {
      setWhatsappConfirmationError(err.response?.data?.error || 'Invalid confirmation code.');
    } finally {
      setWhatsappConfirmationLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Checking session...</p>
      </div>
    );
  }

  if (!user) return null;

  const bookedShiftIds = new Set(bookings.map((b) => b.shift_id));
  const bookingRoleByShiftId = new Map(bookings.map((booking) => [booking.shift_id, booking.assigned_role] as const));
  const hasBookedShifts = bookings.length > 0;
  const availableShifts = shifts.filter((s) => !bookedShiftIds.has(s.id));
  const bookedShifts = shifts.filter((s) => bookedShiftIds.has(s.id));

  const formatDateHeading = (dateKey: string) => {
    const [year, month, day] = dateKey.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return new Intl.DateTimeFormat('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  return (
    <>
      <Header user={user} onLogout={handleLogout} hasBookings={hasBookedShifts} sickNoteEnabled={displaySettings.sick_note_enabled} isLoadingBookings={loading} />

      {showWhatsAppModal && displaySettings.whatsapp_group_join_url && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-emerald-200 bg-white p-6 shadow-2xl relative">
              {/* Booking modal must not be closable via X. User must confirm code or cancel back to dashboard. */}
            <h2 className="text-2xl font-bold text-emerald-900">Join the WhatsApp group</h2>
            <p className="mt-3 text-sm text-gray-700">
              We could not find you on WhatsApp. Please join the volunteer group so we can share practical updates and coordination messages. <strong>You cannot book shifts until you have joined the group.</strong>
            </p>
            <p className="mt-3 text-sm text-gray-700">
              After joining, enter the confirmation code from the group description to close this notice.
            </p>
            <p className="mt-3 text-sm text-gray-700">
              If you have any issues, email us at{' '}
              <a href="mailto:roskilde@connectingworlds.dk" className="underline font-semibold">roskilde@connectingworlds.dk</a>.
            </p>
            <div className="mt-5">
              <a
                href={displaySettings.whatsapp_group_join_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 font-semibold text-white hover:bg-emerald-700"
              >
                Join WhatsApp Group
              </a>
            </div>
            {displaySettings.whatsapp_group_confirmation_code ? (
              <form onSubmit={handleWhatsAppConfirmation} className="mt-6 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirmation code</label>
                  <input
                    type="text"
                    value={whatsappConfirmationCode}
                    onChange={(e) => setWhatsappConfirmationCode(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    placeholder="Enter the code from the WhatsApp group description"
                  />
                </div>
                {whatsappConfirmationError && (
                  <p className="text-sm text-red-700">{whatsappConfirmationError}</p>
                )}
                <div className="space-y-2">
                  <button
                    type="submit"
                    disabled={whatsappConfirmationLoading}
                    className="w-full rounded-lg bg-emerald-600 px-4 py-2 font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                  >
                    {whatsappConfirmationLoading ? 'Checking code...' : 'Confirm and close'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowWhatsAppModal(false);
                      navigate('/volunteer-dashboard');
                    }}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel and go back to dashboard
                  </button>
                </div>
              </form>
            ) : (
              <div className="mt-6 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900">
                The confirmation code is not configured yet. Please ask an admin to add it in the settings.
              </div>
            )}
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && <div className="bg-red-100 text-red-800 p-4 rounded mb-4">{error}</div>}

        {hasBookedShifts ? (
          // Show booked shifts only
          <div>
            <h1 className="text-2xl font-bold mb-6">My Booked Shifts</h1>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-800">
                ✅ You have successfully booked {bookedShifts.length} shift(s). See you at the festival!
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-800 text-sm">
                While not possible to change shifts directly, you can swap shifts with another volunteer before the festival starts.
                Find someone via the WhatsApp group, then both email us at{' '}
                <a href="mailto:roskilde@connectingworlds.dk" className="underline font-medium">roskilde@connectingworlds.dk</a>{' '}
                stating which shifts to swap. The swap is only official after we confirm it to you.
              </p>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Loading shifts...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {bookedShifts.map((shift) => (
                  <ShiftCard
                    key={shift.id}
                    shift={shift}
                    isBooked={true}
                    bookedRole={bookingRoleByShiftId.get(shift.id)}
                    showTitle={displaySettings.show_shift_title}
                    showLocation={displaySettings.show_shift_location}
                    showAvailableSpots={displaySettings.show_available_spots}
                    userRole={user?.role}
                    dateFormat="pretty"
                    timeLayout="below"
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          // Show selection mode
          <div>
            <div className="mb-6">
              <h1 className="text-2xl font-bold mb-2">Select Your Shifts</h1>
              <p className="text-gray-600">
                Select the shifts you want to work. You must satisfy all requirements below before booking. 
                Once booked, changes cannot be made.
              </p>
              {user?.role && (
                <div className={`mt-3 inline-block px-4 py-2 rounded-lg text-sm font-medium ${
                  user.role === 'volunteer' ? 'bg-orange-100 text-orange-800' :
                  user.role === 'supervisor' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  Showing {user.role.charAt(0).toUpperCase() + user.role.slice(1)} shifts only
                </div>
              )}
            </div>

            {/* Constraints Display */}
            {constraints.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-3">Requirements:</h3>
                <div className="space-y-2">
                  {validation ? (
                    validation.constraints.map((c: any, idx: number) => (
                      <div key={idx} className="flex items-start gap-2">
                        <span className={c.satisfied ? 'text-green-600' : 'text-red-600'}>
                          {c.satisfied ? '✓' : '✗'}
                        </span>
                        <span className={c.satisfied ? 'text-green-800' : 'text-red-800'}>
                          {c.message}
                        </span>
                      </div>
                    ))
                  ) : (
                    constraints.map((c: any) => {
                      const hasMin = c.min_shifts !== undefined && c.min_shifts !== null && c.min_shifts > 0;
                      const hasMax = c.max_shifts !== undefined && c.max_shifts !== null && c.max_shifts > 0;
                      
                      if (!hasMin && !hasMax) return null;

                      const isSame = hasMin && hasMax && c.min_shifts === c.max_shifts;

                      return (
                        <div key={c.id} className="flex items-start gap-2 text-gray-700">
                          <span>•</span>
                          <span>
                            {c.tag_name ? (
                              <>
                                {isSame ? (
                                  <>{c.min_shifts} {c.tag_name} shift(s)</>
                                ) : (
                                  <>
                                    {hasMin && `Min ${c.min_shifts}`}
                                    {hasMin && hasMax && ' - '}
                                    {hasMax && `Max ${c.max_shifts}`}
                                    {' '}{c.tag_name} shift(s)
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                {isSame ? (
                                  <>{c.min_shifts} total shift(s)</>
                                ) : (
                                  <>
                                    {hasMin && `Min ${c.min_shifts}`}
                                    {hasMin && hasMax && ' - '}
                                    {hasMax && `Max ${c.max_shifts}`}
                                    {' '}total shift(s)
                                  </>
                                )}
                              </>
                            )}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {displaySettings.booking_enabled !== 'booking-enabled' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-yellow-800 font-medium">
                  ⚠️ {displaySettings.booking_enabled === 'no-access' ? 'Shift access not available' : 'Shift booking is currently disabled'}
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  {displaySettings.booking_enabled === 'no-access' 
                    ? 'Shift viewing is not yet available. Please check back later.'
                    : 'You can view available shifts, but booking is temporarily unavailable. Please check back later.'}
                </p>
              </div>
            )}

            {pendingSickNotes > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-blue-900 font-medium">Pending sick note request{pendingSickNotes > 1 ? 's' : ''}</p>
                    <p className="text-sm text-blue-800 mt-1">
                      You have {pendingSickNotes} sick note request{pendingSickNotes > 1 ? 's' : ''} pending review. We’ll notify you when it’s processed.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/sick-note')}
                    className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
                  >
                    View sick note
                  </button>
                </div>
              </div>
            )}

            {selectedShiftIds.size > 0 && (
              <div className={`border rounded-lg p-4 mb-6 ${
                validation?.valid ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${
                      validation?.valid ? 'text-green-800' : 'text-yellow-800'
                    }`}>
                      {selectedShiftIds.size} shift(s) selected
                      {validation?.valid && ' - Ready to book! ✓'}
                      {validation && !validation.valid && ' - Requirements not met'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedShiftIds(new Set())}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                      Clear Selection
                    </button>
                    <button
                      onClick={handleBookSelectedShifts}
                      disabled={isBooking || !validation?.valid || displaySettings.booking_enabled !== 'booking-enabled'}
                      className={`px-6 py-2 rounded font-semibold ${
                        validation?.valid && displaySettings.booking_enabled === 'booking-enabled'
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      } disabled:bg-gray-400 disabled:text-gray-200`}
                    >
                      {isBooking ? 'Booking...' : 'Book Selected Shifts'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Loading shifts...</p>
              </div>
            ) : availableShifts.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No available shifts</p>
            ) : (
              <div>
                {Object.entries(
                  availableShifts.reduce((acc, shift) => {
                    // Extract date from ISO string without locale conversion
                    const dateKey = shift.start_time.split('T')[0];
                    if (!acc[dateKey]) acc[dateKey] = [];
                    acc[dateKey].push(shift);
                    return acc;
                  }, {} as Record<string, typeof availableShifts>)
                ).map(([date, shiftsOnDate]) => (
                  <div key={date} className="mb-8">
                    <h2 className="text-lg font-bold text-gray-900 mb-4 sticky top-0 bg-white py-2 px-4 border-b">{formatDateHeading(date)}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {shiftsOnDate.map((shift) => {
                        const bookingCapacityPercent = Math.max(0, Math.min(100, Number(displaySettings.booking_capacity_percent ?? 100)));
                        const userRole = user?.role || 'volunteer';
                        const roleReq = shift.role_requirements?.find(req => req.role_name === userRole);
                        const effectiveRoleMax = roleReq
                          ? (userRole === 'volunteer'
                            ? Math.ceil((roleReq.max_count || 0) * (bookingCapacityPercent / 100))
                            : roleReq.max_count)
                          : null;
                        const totalMaxRoleSpots = shift.role_requirements?.reduce((sum, req) => {
                          const maxCount = req.role_name === 'volunteer'
                            ? Math.ceil((req.max_count || 0) * (bookingCapacityPercent / 100))
                            : (req.max_count || 0);
                          return sum + maxCount;
                        }, 0) || 0;
                        const isManuallyFull = shift.manual_full === true;
                        const isFull = isManuallyFull || (roleReq
                          ? (roleReq.current_count || 0) >= (effectiveRoleMax || 0)
                          : (totalMaxRoleSpots > 0 ? shift.current_volunteers >= totalMaxRoleSpots : false));
                        const isPast = new Date(shift.end_time) < new Date();
                        const canInteract = displaySettings.booking_enabled === 'booking-enabled' && !isFull && !isPast && !isManuallyFull;
                        const adjustedShift = {
                          ...shift,
                          role_requirements: shift.role_requirements
                            ? shift.role_requirements.map((req) => (
                              req.role_name === 'volunteer'
                                ? { ...req, max_count: Math.ceil((req.max_count || 0) * (bookingCapacityPercent / 100)) }
                                : req
                            ))
                            : shift.role_requirements,
                        };
                        
                        return (
                          <div
                            key={shift.id}
                            onClick={() => {
                              // Allow deselecting a shift even if it became disabled after refresh
                              if (selectedShiftIds.has(shift.id)) {
                                toggleShiftSelection(shift.id);
                                return;
                              }
                              if (canInteract) {
                                toggleShiftSelection(shift.id);
                              }
                            }}
                            className={`transition-all ${
                              isPast
                                ? 'opacity-50 cursor-not-allowed'
                                : isManuallyFull || isFull
                                  ? 'opacity-60 cursor-not-allowed'
                                  : displaySettings.booking_enabled === 'booking-enabled'
                                    ? `cursor-pointer ${selectedShiftIds.has(shift.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`
                                    : 'cursor-not-allowed opacity-75'
                            }`}
                          >
                            <ShiftCard
                              shift={adjustedShift}
                              canBook={false}
                              isSelected={selectedShiftIds.has(shift.id) && !isPast}
                              borderColor={hasMinTimeViolation(shift.id) ? 'border-2 border-red-500' : undefined}
                              showTitle={displaySettings.show_shift_title}
                              showLocation={displaySettings.show_shift_location}
                              showAvailableSpots={displaySettings.show_available_spots}
                              userRole={user?.role}
                              showDate={false}
                            />
                            {isPast && (
                              <div className="text-xs text-gray-500 mt-1 italic">Shift has ended</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Duplicate selection box at bottom */}
            {selectedShiftIds.size > 0 && (
              <div className={`border rounded-lg p-4 mt-6 ${
                validation?.valid ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${
                      validation?.valid ? 'text-green-800' : 'text-yellow-800'
                    }`}>
                      {selectedShiftIds.size} shift(s) selected
                      {validation?.valid && ' - Ready to book! ✓'}
                      {validation && !validation.valid && ' - Requirements not met'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedShiftIds(new Set())}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                      Clear Selection
                    </button>
                    <button
                      onClick={handleBookSelectedShifts}
                      disabled={isBooking || !validation?.valid || displaySettings.booking_enabled !== 'booking-enabled'}
                      className={`px-6 py-2 rounded font-semibold ${
                        validation?.valid && displaySettings.booking_enabled === 'booking-enabled'
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      } disabled:bg-gray-400 disabled:text-gray-200`}
                    >
                      {isBooking ? 'Booking...' : 'Book Selected Shifts'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Confirm Shift Booking</h2>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <p className="text-gray-700 mb-4">
                You are about to book <strong>{selectedShiftIds.size}</strong> shift(s). This action cannot be undone.
              </p>

              {/* Withdrawal Warning */}
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex gap-3">
                  <svg className="w-6 h-6 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="font-bold text-yellow-900 mb-2">Important: No Withdrawal After Booking</h4>
                    <p className="text-sm text-yellow-800 mb-2">
                      Once you book your shifts, you <strong>cannot withdraw from volunteering unless you find a replacement volunteer</strong>.
                    </p>
                    <p className="text-sm text-yellow-800">
                      According to the volunteer terms you accepted when signing up, <strong>Roskilde Festival may charge a fine of 4500 DKK and ban you from volunteering for 3 years</strong> if you fail to complete your assigned shifts.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Selected Shifts:</h3>
                <div className="space-y-2">
                  {shifts
                    .filter(s => selectedShiftIds.has(s.id))
                    .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
                    .map(shift => {
                      // Extract date/time from ISO string without locale conversion
                      const startStr = shift.start_time.replace('T', ' ').split('.')[0];
                      const endStr = shift.end_time.replace('T', ' ').split('.')[0];
                      const startDate = startStr.split(' ')[0];
                      const endDate = endStr.split(' ')[0];
                      const startTime = startStr.split(' ')[1]; // HH:MM:SS
                      const endTime = endStr.split(' ')[1];
                      const dateLabel = formatDateHeading(startDate);
                      const nextDaySuffix = startDate !== endDate ? ' (next day)' : '';
                      return (
                        <div key={shift.id} className="flex items-start gap-2 text-sm">
                          <span className="text-green-600 mt-0.5">✓</span>
                          <div>
                            <div className="font-medium text-gray-900">
                              {displaySettings.show_shift_title && shift.title ? `${shift.title} · ` : ''}{dateLabel} · {startTime?.substring(0, 5)} - {endTime?.substring(0, 5)}{nextDaySuffix}
                            </div>
                            {displaySettings.show_shift_location && shift.location && (
                              <div className="text-gray-600 text-xs">📍 {shift.location}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
            <div className="p-6 border-t bg-gray-50 flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-6 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmBooking}
                className={`px-6 py-2 rounded font-medium text-white ${
                  user?.role === 'volunteer' ? 'bg-orange-600 hover:bg-orange-700' :
                  user?.role === 'supervisor' ? 'bg-yellow-600 hover:bg-yellow-700' :
                  'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                Confirm Booking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full">
                <span className="text-3xl">✓</span>
              </div>
              <h2 className="text-xl font-bold text-center text-gray-900 mb-2">Booking Successful!</h2>
              <p className="text-gray-700 text-center mb-6">
                Your shifts have been booked successfully. You can view them in your dashboard.
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className={`w-full px-6 py-2 rounded font-medium text-white ${
                  user?.role === 'volunteer' ? 'bg-orange-600 hover:bg-orange-700' :
                  user?.role === 'supervisor' ? 'bg-yellow-600 hover:bg-yellow-700' :
                  'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                <span className="text-3xl text-red-600">✗</span>
              </div>
              <h2 className="text-xl font-bold text-center text-gray-900 mb-2">Error</h2>
              <p className="text-gray-700 text-center mb-6">
                {modalMessage}
              </p>
              <button
                onClick={() => setShowErrorModal(false)}
                className="w-full px-6 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
